import React, {useMemo, useRef, useState} from "react";
import Form, {FormInputComponent} from "../../../components/form/Form";
import {Badge, DatePicker, InputNumber, Table} from "antd";
import UserImageCircle from "../../../components/UserImageCircle";
import SpinLoader from "../../../components/SpinLoader";
import {useAuth} from "../../../providers/AuthProvider";
import ReactDOM from "react-dom";
import AnimatedCount from "../../../components/AnimatedCount";
import {useTalentCandidateScope} from "./TalentCandidateScopeProvider";
import {usePosition} from "../../../providers/ProjectPositionProvider";
import {useQueryClient} from "@tanstack/react-query";
import CandidateConfirmPayoutForm from "./CandidateConfirmPayoutForm";
import {useLocation} from "react-router-dom";

const CandidatePayoutForm = ({forAgency, ...props}) => {

    const {
        candidate,
        data,
        isLoading: isLoadingCandidate,
        isDisabled,
        onUpdateProposal,
        onChange,
        updateCandidateScope,
        confirmTalentScope,
        resetModel
    } = useTalentCandidateScope();

    const location = useLocation();
    const queryClient = useQueryClient();

    const {position} = usePosition();

    const {auth} = useAuth();

    const formRef = useRef(null);


    let mapFormModel = (model) => {

        let mapped = {...model};

        mapped.display_percent_markup = parseFloat(mapped.percent_markup) * 100;

        if (position.fixedFeePricingStrategyIsFullBreakdown()) {
            mapped.hourly_rate = parseFloat(mapped.hourly_rate)
            mapped.platform_rate = (parseFloat(mapped.hourly_rate) * (1 + parseFloat(mapped.percent_markup)))
        }


        // mapped.proposed_total_periods = mapped.proposed_total_periods;
        if (!position.fixedFeePricingStrategyIsTotalPayout()) {
            mapped.total_hours = TB.sumBy(mapped.position_deliverables, del => parseInt(del.total_hours || 0));
            mapped.payout_amount = TB.sumBy(mapped.position_deliverables, del => parseFloat(del.payout_amount || 0));
        }

        // mapped.proposed_payout_amount = Math.ceil(mapped.proposed_payout_amount || mapped.payout_amount);


        mapped.platform_cost = Math.ceil((parseFloat(mapped.payout_amount) * (1 + parseFloat(mapped.percent_markup))));

        console.log('mapFormModel changed', mapped.platform_cost)
        return mapped;
    }

    const [formModel, setFormModel] = useState(() => {
        let opp = TB.cloneDeep({...candidate.job_candidate.talent_opps[0]});
        return mapFormModel({
            percent_markup: candidate.job_candidate.percent_markup,
            ...opp
        })
    });

    const hasFormModelChanged = useMemo(() => {
        if (isLoadingCandidate || !candidate) {
            return false;
        }
        let originalOpp = candidate.job_candidate.talent_opps[0];

        if (parseFloat(originalOpp.hourly_rate) !== parseFloat(formModel.hourly_rate)
            || candidate?.job_candidate?.percent_markup !== formModel.percent_markup
            || candidate?.job_candidate?.payout_amount !== formModel.payout_amount) {
            return true;
        }


        return TB.find(formModel.position_deliverables || [], del => {
            let matchedDeliverable = TB.find(originalOpp.position_deliverables, ['id', parseInt(del.id)]);

            return !matchedDeliverable || (parseInt(matchedDeliverable.total_hours) !== parseInt(del.total_hours)) ||
                !TB.moment(del.target_delivery_at).isSame(matchedDeliverable.target_delivery_at, 'day') ||
                (parseFloat(matchedDeliverable.payout_amount) !== parseFloat(del.payout_amount))
        })
    }, [
        isLoadingCandidate,
        isDisabled,
        formModel.hourly_rate,
        formModel.payout_amount,
        formModel.total_hours,
        ...TB.map(formModel.position_deliverables || [], 'target_delivery_at'),
        ...TB.map(formModel.position_deliverables || [], 'total_hours'),
        ...TB.map(formModel.position_deliverables || [], 'payout_amount'),
        formModel.percent_markup
    ]);

    const [candidateStatusLabel, candidateStatusColor] = useMemo(() => {
        return candidate?.scopeStatusForPosition?.(position) || ['', ''];

    }, [candidate, isDisabled]);

    const onCancel = async () => {
        if (isDisabled) return;

        let opp = TB.cloneDeep(candidate.job_candidate.talent_opps[0]);
        return setFormModel(mapFormModel({
            percent_markup: candidate.job_candidate.percent_markup,
            ...opp
        }))
    }

    const onSubmit = async () => {
        if (isDisabled) return;


        console.log('onSubmit')
        let invalidFields = await formRef.current?.forceValidate();
        if (invalidFields) {
            const node = ReactDOM.findDOMNode(invalidFields);

            $('.ant-modal-wrap')?.animate({scrollTop: $(node).offset().top}, 1000);
        }
        if (!formModel.payout_amount) {
            return TB.toast('Form Invalid', {type: 'error'})
        }

        let position_deliverables = TB.map(formModel.position_deliverables, del => {
            let {
                target_delivery_at,
                total_hours,
                payout_amount,
                ...delProps
            } = {...del}
            let updatedDel = {
                ...delProps,
                target_delivery_at: target_delivery_at ? TB.moment(target_delivery_at).toISOString() : null
            }
            if (!position.fixedFeePricingStrategyIsTotalPayout()) {
                updatedDel = {
                    ...updatedDel,
                    total_hours: total_hours ? parseInt(total_hours) : null,
                    payout_amount: parseFloat(payout_amount),
                }
            }
            return updatedDel
        })
        let params = {
            position_deliverables,
            percent_markup: formModel.percent_markup,
        }
        if (position.fixedFeePricingStrategyIsTotalPayout() && formModel.payout_amount) {
            params.payout_amount = formModel.payout_amount;
        }else if (position.fixedFeePricingStrategyIsFullBreakdown() && formModel.hourly_rate) {
            params.hourly_rate = formModel.hourly_rate;
        }
        let response = await updateCandidateScope(params);

        if (response) {
            console.log('onSubmit', response)
            return;
        }
    }

    if (!candidate || isLoadingCandidate) return null;

    return (
        <SpinLoader isLoading={isDisabled}>
            <div className="flex flex-col items-stretch">
                <div className="pb-4 px-6">
                    <Form
                        ref={formRef}
                        onSubmit={onSubmit}
                        minimal
                        submitButton={false}
                        background={"none"}
                        isDisabled={isDisabled}
                        model={formModel}
                        onChange={(model) => {
                            let updatedFormModel = {...formModel};

                            for (let [key, value] of Object.entries({...model})) {
                                if (key === 'display_percent_markup') {
                                    updatedFormModel = TB.set(updatedFormModel, 'percent_markup', parseFloat(value || 0) / 100);
                                } else {
                                    updatedFormModel = TB.set(updatedFormModel, key, value);
                                }
                            }

                            return setFormModel(mapFormModel(updatedFormModel))
                        }}
                        fields={[]}
                    >

                        <div className="flex gap-4">

                            <div className="w-full">
                                <div className="flex gap-4 pb-2 border-b border-wripplePrimaryRuleBlue mb-4">
                                    {location.pathname.includes('shortlist') ? (
                                        <div className="basis-1/4 flex flex-row gap-4 p-3">
                                            <div className="flex-1 flex flex-col gap-2">
                                                <p className="text-sm font-medium text-left">
                                                    Pricing Strategy
                                                </p>
                                                <div className="h-[30px] flex flex-row items-center gap-2">
                                                    <Badge
                                                        count={`${position.getFixedFeePricingStrategyName()}`}
                                                        color={'#9f9f9f'}/>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="basis-1/4 flex flex-row gap-4 p-3">
                                            <div className="flex-1 flex flex-col gap-2">
                                                <p className="text-sm font-medium text-left">
                                                    Candidate
                                                </p>

                                                {candidate?.id && (
                                                    <div className="flex flex-row items-center gap-4">
                                                        <UserImageCircle user={candidate} size={40}/>

                                                        <div className="flex flex-col items-start justify-center gap-1.5">
                                                            <p className="font-medium text-sm whitespace-no-wrap">
                                                                {candidate.fullName()}
                                                            </p>
                                                        </div>

                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                    )}
                                    <div className="basis-1/4 flex flex-row gap-4 p-3">
                                        <div className="flex-1 flex flex-col gap-2">
                                            <p className="text-sm font-medium text-left">
                                                Status
                                            </p>

                                            <div className="h-[30px] flex flex-row items-center gap-2">
                                                {candidate.hasConfirmedScope() ? (
                                                    <Badge count={candidateStatusLabel} showZero color={candidateStatusColor}/>
                                                ) : (
                                                    <p className="leading-[30px] font-medium">
                                                        <i>Scoping Required</i>
                                                    </p>
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                    {position.fixedFeePricingStrategyIsFullBreakdown() && (
                                        <div className="basis-1/4 flex flex-row gap-4 p-3">
                                            <div className="flex-1 max-w-[300px] flex flex-col items-start gap-2">
                                                {/*<p className="text-sm font-medium text-left">*/}
                                                {/*    Talent Rate*/}
                                                {/*</p>*/}
                                                <p className="text-sm font-medium text-left flex flex-row items-center gap-2">
                                                    Proposed Talent Rate <i className={TB.mapClass("fas", {
                                                    "fa-check-circle text-gray": !candidate.hasConfirmedScope() || !formModel.hourly_rate || formModel.hourly_rate === candidate.job_candidate.standard_hourly_rate,
                                                    "fa-exclamation-triangle text-warning": candidate.hasConfirmedScope() && formModel.hourly_rate && formModel.hourly_rate !== candidate.job_candidate.standard_hourly_rate
                                                })}/>
                                                </p>
                                                <FormInputComponent
                                                    fieldName={`hourly_rate`}
                                                    value={formModel?.hourly_rate || ''}
                                                    disabled={isDisabled}
                                                    required={true}
                                                    label={false}
                                                    type="antd-input">

                                                    <InputNumber
                                                        controls={false}
                                                        min={1}
                                                        addonBefore="$"
                                                        addonAfter="/hr"
                                                        precision={2}
                                                        defaultValue={formModel?.hourly_rate || ''}/>

                                                </FormInputComponent>
                                            </div>
                                        </div>
                                    )}
                                    {/*{auth.canManageClients() ? (*/}
                                    {/*    <div className="basis-1/4 bg-gray-50 flex flex-row gap-4 self-start p-3">*/}

                                    {/*        <div className="flex-1 flex flex-col items-start gap-2">*/}
                                    {/*            <p className="text-sm font-medium text-left flex flex-row items-center gap-2">*/}
                                    {/*                Proposed Talent Rate <i className={TB.mapClass("fas", {*/}
                                    {/*                "fa-check-circle text-gray": !candidate.hasConfirmedScope() || !formModel.hourly_rate || formModel.hourly_rate === candidate.job_candidate.standard_hourly_rate,*/}
                                    {/*                "fa-exclamation-triangle text-warning": candidate.hasConfirmedScope() && formModel.hourly_rate && formModel.hourly_rate !== candidate.job_candidate.standard_hourly_rate*/}
                                    {/*            })}/>*/}
                                    {/*            </p>*/}
                                    {/*            <p className="h-[30px] leading-[30px] font-medium">*/}
                                    {/*                {candidate.hasConfirmedScope() ?*/}
                                    {/*                    `$${TB.formatNumber(formModel?.hourly_rate, 2)}/hr` :*/}
                                    {/*                    (*/}
                                    {/*                        <i className="opacity-50">N/A</i>*/}
                                    {/*                    )*/}
                                    {/*                }*/}
                                    {/*            </p>*/}
                                    {/*        </div>*/}
                                    {/*    </div>*/}
                                    {/*) : (*/}
                                    {/*    <div className="basis-1/4"/>*/}
                                    {/*)}*/}
                                    <div className="basis-1/4 flex flex-row gap-4 p-3">

                                        <div className="flex-1 max-w-[300px] flex flex-col items-start gap-2">
                                            <p className="text-sm font-medium text-left">
                                                Percent Markup
                                            </p>
                                            <FormInputComponent
                                                fieldName={`display_percent_markup`}
                                                value={formModel?.display_percent_markup || ''}
                                                disabled={isDisabled}
                                                required={true}
                                                label={false}
                                                type="antd-input">

                                                <InputNumber
                                                    controls={false}
                                                    min={0}
                                                    step={1}
                                                    addonAfter="%"
                                                    precision={4}
                                                    defaultValue={formModel.display_percent_markup || 0}
                                                    formatter={value => Number(value)}
                                                />
                                            </FormInputComponent>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-4 pb-4 border-b border-wripplePrimaryRuleBlue mb-6">

                                    {position.fixedFeePricingStrategyIsFullBreakdown() && (
                                        <React.Fragment>
                                            {auth.canManageClients() ? (
                                                <div className="flex-1 bg-gray-50 flex flex-row gap-4 p-3">
                                                    <div className="flex-1 max-w-[300px] flex flex-col items-start gap-2">
                                                        <p className="text-sm font-medium text-left flex flex-row items-center gap-2">
                                                            Proposed Est. Hours <i className={TB.mapClass("fas", {
                                                            "fa-check-circle text-gray": !candidate.hasConfirmedScope() || !formModel?.total_hours || formModel.total_hours === candidate.job_candidate.standard_total_periods,
                                                            "fa-exclamation-triangle text-warning": candidate.hasConfirmedScope() && formModel?.total_hours && formModel.total_hours !== candidate.job_candidate.standard_total_periods
                                                        })}/>
                                                        </p>

                                                        {/*<p className="h-[30px] leading-[30px] font-medium">*/}
                                                        {/*{candidate.hasConfirmedScope() ? (*/}
                                                        {/*    <span>{formModel?.proposed_total_periods || formModel?.total_hours} hours</span>*/}
                                                        {/*) : (*/}
                                                        {/*    <i className="opacity-50">N/A</i>*/}
                                                        {/*)}*/}
                                                        {/*</p>*/}
                                                        <AnimatedCount value={formModel?.total_hours}
                                                                       className="h-[30px] leading-[30px] font-medium">
                                                            {formModel?.total_hours} hours
                                                        </AnimatedCount>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex-1"/>
                                            )}
                                        </React.Fragment>
                                    )}
                                    {auth.canManageClients() ? (
                                        <div className="flex-1 bg-gray-50 flex flex-row gap-4 p-3">

                                            <div className="flex-1 max-w-[300px] flex flex-col items-start gap-2">
                                                <p className="text-sm font-medium text-left flex flex-row items-center gap-2">
                                                    Proposed Payout <i className={TB.mapClass("fas", {
                                                    "fa-check-circle text-gray": !candidate.hasConfirmedScope() || !formModel.payout_amount || formModel.payout_amount === candidate.job_candidate?.standard_payout_amount,
                                                    "fa-exclamation-triangle text-warning": candidate.hasConfirmedScope() && formModel.payout_amount && formModel.payout_amount !== candidate.job_candidate?.standard_payout_amount
                                                })}/>
                                                </p>

                                                {position.fixedFeePricingStrategyIsTotalPayout() ? (
                                                    <FormInputComponent
                                                        fieldName={`payout_amount`}
                                                        value={formModel?.payout_amount || ''}
                                                        disabled={isDisabled}
                                                        required={true}
                                                        label={false}
                                                        type="antd-input">

                                                        <InputNumber
                                                            controls={false}
                                                            min={1}
                                                            addonBefore="$"
                                                            precision={2}
                                                            defaultValue={formModel?.payout_amount || ''}/>

                                                    </FormInputComponent>
                                                ) : (
                                                    <AnimatedCount value={formModel?.payout_amount}
                                                                   className="h-[30px] leading-[30px] font-medium">
                                                        {formModel?.payout_amount ? TB.formatCurrency(formModel?.payout_amount, false) : 'N/A'}
                                                    </AnimatedCount>
                                                )}

                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex-1"/>
                                    )}

                                    {position.fixedFeePricingStrategyIsFullBreakdown() && (
                                        <div className="flex-1 flex flex-row gap-4 p-3">

                                            <div className="flex-1 max-w-[300px] flex flex-col items-start gap-2">
                                                <p className="text-sm font-medium text-left text-info">
                                                    Assigned Bill Rate
                                                </p>

                                                <AnimatedCount value={formModel?.platform_rate}
                                                               className="h-[30px] leading-[30px] font-medium">
                                                    {
                                                        `$${TB.formatNumber(
                                                            formModel.platform_rate,
                                                            2
                                                        )}/hr`
                                                    }
                                                </AnimatedCount>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex-1 flex flex-row gap-4 p-3">

                                        <div className="flex-1 max-w-[300px] flex flex-col items-start gap-2">
                                            <p className="text-sm font-medium text-left text-info">
                                                Client Fee
                                            </p>

                                            <AnimatedCount
                                                value={formModel.platform_cost}
                                                className="h-[30px] leading-[30px] font-medium">

                                                {TB.formatCurrency(formModel.platform_cost, false)}

                                            </AnimatedCount>
                                        </div>

                                    </div>

                                </div>

                                <div className="flex flex-col gap-4 pb-2 border-b border-wripplePrimaryRuleBlue mb-4">
                                    <p className="text-base font-semibold">
                                        Estimated Hours By Deliverable
                                    </p>
                                    <div className="flex-grow px-4">
                                        <Table size="small" pagination={false} rowKey='name'
                                               dataSource={[...formModel.position_deliverables]}
                                               summary={data => {

                                                   const oppDeliverableIds = TB.map(formModel.position_deliverables || [], 'id')

                                                   let matchedPositionDeliverables = TB.filter(
                                                       position.deliverables,
                                                       del => oppDeliverableIds.includes(del.id)
                                                   )

                                                   return (
                                                       <Table.Summary.Row>
                                                           <Table.Summary.Cell index={0} className="bg-gray-50"/>
                                                           <Table.Summary.Cell index={1} className="bg-gray-50"/>
                                                           <Table.Summary.Cell index={2} className="bg-gray-50"/>


                                                           {!position.fixedFeePricingStrategyIsTotalPayout() && (
                                                               <React.Fragment>
                                                                   <Table.Summary.Cell index={3} className="bg-gray-50">
                                                                       <p className="font-semibold text-xs">
                                                                           Totals
                                                                       </p>
                                                                   </Table.Summary.Cell>
                                                                   <Table.Summary.Cell index={4} className="bg-gray-50">
                                                                       <p className="whitespace-nowrap text-xs">
                                                                           {TB.sumBy(matchedPositionDeliverables, 'total_hours')} hours
                                                                       </p>
                                                                   </Table.Summary.Cell>
                                                                   <Table.Summary.Cell index={5} className="bg-gray-50">
                                                                       <p className="font-semibold text-xs whitespace-nowrap">
                                                                           {TB.sumBy(data, del => parseInt(del.total_hours || 0))} hours
                                                                       </p>
                                                                   </Table.Summary.Cell>
                                                                   <Table.Summary.Cell index={6} className="bg-gray-50">
                                                                       <p className="whitespace-nowrap text-xs">
                                                                           ${TB.formatNumber(TB.sumBy(matchedPositionDeliverables, del => parseFloat(del.payout_amount || 0)))}
                                                                       </p>
                                                                   </Table.Summary.Cell>
                                                                   <Table.Summary.Cell index={7} className="bg-gray-50">
                                                                       <p className="font-semibold text-xs whitespace-nowrap">
                                                                           ${TB.formatNumber(TB.sumBy(data, del => parseFloat(del.payout_amount || 0)))}
                                                                       </p>
                                                                   </Table.Summary.Cell>
                                                               </React.Fragment>
                                                           )}
                                                       </Table.Summary.Row>
                                                   )
                                               }}
                                        >

                                            <Table.Column
                                                title={<span className="text-xs">Deliverable</span>}
                                                dataIndex="name"
                                                key="name"
                                                render={(name, record) => (
                                                    <p className="text-sm font-medium text-secondary">
                                                        {name || 'N/A'}
                                                    </p>
                                                )}/>
                                            <Table.Column
                                                title={<span className="text-xs">Description</span>}
                                                dataIndex="description"
                                                key="description"
                                                render={(description) => (
                                                    <div dangerouslySetInnerHTML={{
                                                        __html: description || '<i class="opacity-50">No description provided..</i>'
                                                    }} className="max-w-[300px] text-xs line-clamp-2"/>
                                                )}/>
                                            <Table.Column
                                                title={<span className="text-xs whitespace-nowrap">
                                                    Target Date
                                                </span>}
                                                dataIndex="completed_at"
                                                width={110}
                                                render={(_, record) => {
                                                    let matchedPositionDeliverable = TB.find(position.deliverables, ['id', record.id]);
                                                    const completedAt = TB.moment().add(TB.sample([1, 3, 5, 6, 10]), 'weeks');

                                                    return (
                                                        <p className="text-xs">
                                                            {matchedPositionDeliverable.target_delivery_at ? TB.moment(matchedPositionDeliverable.target_delivery_at).format('ll') : (
                                                                <i>Date Not Provided</i>
                                                            )}
                                                        </p>
                                                    )
                                                }}
                                            />
                                            <Table.Column
                                                title={<span className="text-xs text-danger"><span className="whitespace-nowrap">Talent Delivery Date</span> <i>(optional)</i></span>}
                                                dataIndex="target_delivery_at"
                                                width={110}
                                                render={(target_delivery_at, deliverable, index) => {

                                                    return (
                                                        <div className="w-32">
                                                            <FormInputComponent
                                                                fieldName={`position_deliverables[${index}].target_delivery_at`}
                                                                disabled={isDisabled}
                                                                required={false}
                                                                className="mb-0"
                                                                formatAsDate
                                                                value={deliverable.target_delivery_at ? TB.moment(deliverable.target_delivery_at) : undefined}
                                                                label={false}
                                                                type="antd-input">

                                                                <DatePicker
                                                                    disabledDate={date => false}
                                                                    format="ll"
                                                                    defaultValue={deliverable.target_delivery_at ? TB.moment(deliverable.target_delivery_at) : undefined}
                                                                    size="small"
                                                                />
                                                            </FormInputComponent>
                                                        </div>
                                                    )
                                                }}
                                            />

                                            {!position.fixedFeePricingStrategyIsTotalPayout() && (
                                                <React.Fragment>
                                                    <Table.Column
                                                        title={<span className="text-xs whitespace-nowrap">Est. Hours</span>}
                                                        dataIndex="id"
                                                        key="id"
                                                        render={(_, record) => {

                                                            let matchedPositionDeliverable = TB.find(position.deliverables, ['id', record.id]);

                                                            return (
                                                                <p>
                                                                    {matchedPositionDeliverable?.total_hours ? `${matchedPositionDeliverable.total_hours} hours` : (
                                                                        <i className="opacity-50">N/A</i>
                                                                    )}
                                                                </p>
                                                            )
                                                        }}/>

                                                    <Table.Column
                                                        title={<span className="text-danger text-xs"><span className="whitespace-nowrap">Talent Est. Hours</span> {position.fixedFeePricingStrategyIsFullBreakdown() ? <i>(required)</i> : <i>(optional)</i>}</span>}
                                                        dataIndex="total_hours"
                                                        key="total_hours"
                                                        render={(total_hours, deliverable, index) => (
                                                            <div className="w-32">
                                                                <FormInputComponent
                                                                    fieldName={`position_deliverables[${index}].total_hours`}
                                                                    value={total_hours || ''}
                                                                    disabled={isDisabled}
                                                                    required={position.fixedFeePricingStrategyIsFullBreakdown()}
                                                                    className="mb-0"
                                                                    label={false}
                                                                    placeholder={'--'}
                                                                    type="antd-input">

                                                                    <InputNumber
                                                                        controls={false}
                                                                        min={1}
                                                                        addonAfter="hrs"
                                                                        precision={0}
                                                                        defaultValue={total_hours || ''}/>

                                                                </FormInputComponent>
                                                            </div>
                                                        )}/>
                                                    <Table.Column
                                                        title={<span className="text-xs whitespace-nowrap">Target Payout</span>}
                                                        dataIndex="id"
                                                        key="id"
                                                        render={(_, record) => {

                                                            let matchedPositionDeliverable = TB.find(position.deliverables, ['id', record.id]);

                                                            return (
                                                                <p>
                                                                    {matchedPositionDeliverable?.payout_amount ? `$${TB.formatNumber(matchedPositionDeliverable.payout_amount)}` : (
                                                                        <i className="opacity-50">N/A</i>
                                                                    )}
                                                                </p>
                                                            )
                                                        }}/>

                                                    <Table.Column
                                                        title={<span className="text-danger text-xs whitespace-nowrap">Talent Payout</span>}
                                                        dataIndex="payout_amount"
                                                        key="payout_amount"
                                                        render={(payout_amount, deliverable, index) =>

                                                            position.fixedFeePricingStrategyIsPayoutByDeliverable() ? (
                                                            <div className="w-32">
                                                                <FormInputComponent
                                                                    fieldName={`position_deliverables[${index}].payout_amount`}
                                                                    value={payout_amount || ''}
                                                                    disabled={isDisabled}
                                                                    required={true}
                                                                    className="mb-0"
                                                                    label={false}
                                                                    placeholder={'--'}
                                                                    type="antd-input">

                                                                    <InputNumber
                                                                        controls={false}
                                                                        min={1}
                                                                        addonBefore="$"
                                                                        precision={2}
                                                                        defaultValue={payout_amount || ''}/>

                                                                </FormInputComponent>
                                                            </div>
                                                        ) : (
                                                                <p>
                                                                    {payout_amount ? `$${TB.formatNumber(payout_amount)}` : (
                                                                        <i className="opacity-50">N/A</i>
                                                                    )}
                                                                </p>
                                                            )}/>
                                                </React.Fragment>
                                            )}

                                        </Table>
                                    </div>
                                </div>
                            </div>
                        </div>


                    </Form>
                </div>

                <div className="flex flex-row items-center justify-end gap-4 px-6 py-4 border border-t-gray-300">
                    <CandidateConfirmPayoutForm/>

                    <button onClick={() => onCancel()} disabled={isDisabled || !hasFormModelChanged}
                            className="btn btn-outline-dark">
                        Cancel
                    </button>

                    <button onClick={() => onSubmit()} disabled={isDisabled || !hasFormModelChanged}
                            className="btn btn-primary">
                        Update
                    </button>
                </div>

            </div>
        </SpinLoader>
    )
}

export default CandidatePayoutForm